import { useState } from "react";
import {
  correctionImpact,
  mayScore,
  scoringFor,
  validateResult,
  type Actor,
  type Command,
  type Heat,
  type Match,
  type Pair,
  type Tournament,
} from "@spikeeasy/tournament";
import { Dialog, Field, Notice, teamName } from "./ui";

export function ScoreDialog({
  t,
  match,
  actor,
  send,
  close,
  busy,
  queued,
}: {
  t: Tournament;
  match: Match;
  actor: Actor;
  send: (command: Command) => Promise<void>;
  close: () => void;
  busy: boolean;
  queued: boolean;
}) {
  const [manual, setManual] = useState<string[][] | null>(null);
  const [error, setError] = useState("");
  const [review, setReview] = useState(false);
  const [reason, setReason] = useState("");
  const [reset, setReset] = useState(false);
  const rule = scoringFor(t, match);
  const court = t.courts.find((c) => c.id === match.courtId);
  const correcting = match.status === "complete";
  const editableLive = !correcting && mayScore(t, match, actor);
  const sets =
    manual ??
    (correcting ? match.sets : [...match.sets, match.points]).map((set) =>
      set.map(String),
    );
  const affected = correcting ? correctionImpact(t, match.id) : [];
  const submit = async () => {
    try {
      if (sets.some((set) => set.some((value) => value.trim() === "")))
        throw new Error("Enter both scores for every set.");
      const scores = sets.map(
        (set) => [Number(set[0]), Number(set[1])] as Pair,
      );
      validateResult(scores, rule);
      if (!review) {
        setReview(true);
        setError("");
        return;
      }
      await send(
        correcting
          ? {
              type: "correct",
              matchId: match.id,
              revision: match.revision,
              sets: scores,
              resetDependents: reset,
              reason,
            }
          : {
              type: "finish",
              matchId: match.id,
              revision: match.revision,
              sets: scores,
            },
      );
      close();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "The result could not be saved.",
      );
    }
  };
  const run = async (command: Command) => {
    try {
      await send(command);
      setError("");
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "The score could not be saved.",
      );
    }
  };
  return (
    <Dialog
      title={correcting ? "Correct the result" : "Keep the score"}
      onClose={close}
    >
      <p className="muted">
        {court ? `${court.name} · ` : ""}First to {rule.target}
        {rule.winByTwo ? ", win by two" : ""}
        {rule.cap ? `, cap ${rule.cap}` : ""} ·{" "}
        {rule.setsToWin === 1 ? "One set" : `Best of ${rule.setsToWin * 2 - 1}`}
      </p>
      {queued && (
        <Notice>
          Saved on this device. Waiting for the server to confirm.
        </Notice>
      )}
      {error && <Notice error>{error}</Notice>}
      {editableLive && !review && (
        <div className="scoreboard">
          {([0, 1] as const).map((side) => (
            <div key={side} className="score-side">
              <h3>{teamName(t, match.teams[side])}</h3>
              <button
                className="score-button"
                disabled={busy}
                aria-label={`Point for ${teamName(t, match.teams[side])}. Current score ${match.points[side]}`}
                onClick={() =>
                  run({
                    type: "point",
                    matchId: match.id,
                    revision: match.revision,
                    side: side ? "b" : "a",
                    delta: 1,
                  })
                }
              >
                {match.points[side]}
                <small>+ Point</small>
              </button>
              <button
                className="text-button"
                disabled={busy || match.points[side] === 0}
                onClick={() =>
                  run({
                    type: "point",
                    matchId: match.id,
                    revision: match.revision,
                    side: side ? "b" : "a",
                    delta: -1,
                  })
                }
              >
                Undo point
              </button>
            </div>
          ))}
        </div>
      )}
      {review ? (
        <div className="review">
          <h3>Confirm the score</h3>
          <p>
            {teamName(t, match.teams[0])} vs {teamName(t, match.teams[1])}
          </p>
          <p className="result-score">
            {sets.map((set) => set.join(" : ")).join(" / ")}
          </p>
          <p className="muted">
            {correcting
              ? "The previous result will remain saved with this correction."
              : "Once submitted, only the organizer can change this result."}
          </p>
        </div>
      ) : (
        <details open={!editableLive || manual !== null}>
          <summary>Enter final set scores</summary>
          <div className="stack">
            {sets.map((set, index) => (
              <div className="form-row" key={index}>
                {([0, 1] as const).map((side) => (
                  <Field
                    key={side}
                    label={`Set ${index + 1} · ${teamName(t, match.teams[side])}`}
                  >
                    <input
                      inputMode="numeric"
                      value={set[side] ?? ""}
                      onChange={(e) => {
                        const next = sets.map((v) => [...v]);
                        next[index]![side] = e.target.value;
                        setManual(next);
                      }}
                    />
                  </Field>
                ))}
              </div>
            ))}
            <div className="actions">
              {sets.length < rule.setsToWin * 2 - 1 && (
                <button
                  className="text-button"
                  onClick={() => setManual([...sets, ["", ""]])}
                >
                  Add set
                </button>
              )}
              {sets.length > 1 && (
                <button
                  className="text-button"
                  onClick={() => setManual(sets.slice(0, -1))}
                >
                  Remove last set
                </button>
              )}
            </div>
          </div>
        </details>
      )}
      {correcting && (
        <div className="stack">
          <Field label="Reason for correction">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              minLength={3}
              required
            />
          </Field>
          {affected.length > 0 && (
            <Notice>
              These dependent matches will be recalculated:{" "}
              {affected.join(", ")}. Completed results remain in the history.
            </Notice>
          )}
          {affected.some(
            (id) => t.matches.find((m) => m.id === id)?.startedAt,
          ) && (
            <label className="check">
              <input
                type="checkbox"
                checked={reset}
                onChange={(e) => setReset(e.target.checked)}
              />
              I have reviewed the affected games and confirm resetting their
              play and results.
            </label>
          )}
        </div>
      )}
      <div className="sheet-actions">
        {review && (
          <button className="text-button" onClick={() => setReview(false)}>
            Back
          </button>
        )}
        {!review && editableLive && rule.setsToWin > 1 && (
          <button
            className="secondary"
            disabled={busy}
            onClick={() =>
              run({
                type: "next_set",
                matchId: match.id,
                revision: match.revision,
              })
            }
          >
            Next set
          </button>
        )}
        <button
          className="primary"
          disabled={busy || (correcting && reason.trim().length < 3)}
          onClick={submit}
        >
          {busy ? "Saving…" : review ? "Confirm result" : "Review result"}
        </button>
      </div>
    </Dialog>
  );
}

export function HeatDialog({
  t,
  heat,
  send,
  close,
  busy,
  serverTime,
}: {
  t: Tournament;
  heat: Heat;
  send: (command: Command) => Promise<void>;
  close: () => void;
  busy: boolean;
  serverTime: string;
}) {
  const correcting = heat.status === "complete";
  const [scores, setScores] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(heat.totals ?? {}).map(([id, score]) => [
        id,
        String(score),
      ]),
    ),
  );
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [reset, setReset] = useState(false);
  const ended =
    !!heat.startedAt &&
    Date.parse(serverTime) >=
      Date.parse(heat.startedAt) + t.config.heatMinutes * 60000;
  return (
    <Dialog title={`Heat ${heat.round} · Final totals`} onClose={close}>
      <p>
        Agree on everyone’s score before submitting. Each team’s points carry
        over to the next heat.
      </p>
      {!ended && (
        <Notice>
          The heat is still running. Totals can be submitted after its{" "}
          {t.config.heatMinutes} minutes.
        </Notice>
      )}
      {error && <Notice error>{error}</Notice>}
      <form
        className="stack"
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            const result = {
              heatId: heat.id,
              revision: heat.revision,
              totals: Object.fromEntries(
                heat.teamIds.map((id) => [id, Number(scores[id])]),
              ),
            };
            await send(
              correcting
                ? {
                    type: "correct_heat",
                    ...result,
                    reason,
                    resetDependents: reset,
                  }
                : { type: "finish_heat", ...result },
            );
            close();
          } catch (e) {
            setError(
              e instanceof Error ? e.message : "The heat could not be saved.",
            );
          }
        }}
      >
        {heat.teamIds.map((id) => (
          <Field key={id} label={teamName(t, id)}>
            <input
              type="number"
              min={0}
              max={10000}
              step={1}
              required
              value={scores[id] ?? ""}
              onChange={(e) => setScores({ ...scores, [id]: e.target.value })}
            />
          </Field>
        ))}
        {correcting && (
          <>
            <Field label="Reason for correction">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                required
                minLength={3}
              />
            </Field>
            {t.playoffGenerated && (
              <Notice>
                The playoff draw will be regenerated from the corrected heat
                totals. Previous results remain in organizer history.
              </Notice>
            )}
            {t.matches.some((m) => m.startedAt) && (
              <label className="check">
                <input
                  type="checkbox"
                  checked={reset}
                  onChange={(e) => setReset(e.target.checked)}
                  required
                />
                I confirm resetting the playoff matches that have already
                started.
              </label>
            )}
          </>
        )}
        <button
          className="primary"
          disabled={
            busy ||
            (!correcting && !ended) ||
            (correcting && reason.trim().length < 3)
          }
        >
          {correcting ? "Save correction" : "Confirm all totals"}
        </button>
      </form>
    </Dialog>
  );
}
